import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { BarChart3, Users, TrendingUp, Clock, RefreshCw, Lock, LogOut, AlertCircle } from 'lucide-react';
import { ConversionFunnel } from '@/components/analytics/ConversionFunnel';
import { HeatmapViewer } from '@/components/analytics/HeatmapViewer';
import { useConversionRate, useConversionFunnel, useHeatmapData, useTopClicks, useVisitorsOverTime, useAverageSessionTime } from '@/hooks/usePublicAnalytics';
import { usePublicTracking } from '@/hooks/usePublicTracking';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const PASSWORD = 'LyraMarketing';
const SESSION_KEY = 'mapa_authenticated';

const getDateRange = (preset: string) => {
  const end = new Date();
  const start = new Date();
  
  switch (preset) {
    case '30m':
      start.setMinutes(start.getMinutes() - 30);
      break;
    case '1h':
      start.setHours(start.getHours() - 1);
      break;
    case '3h':
      start.setHours(start.getHours() - 3);
      break;
    case 'today':
      start.setHours(0, 0, 0, 0);
      break;
    case '7d':
      start.setDate(start.getDate() - 7);
      break;
    case '30d':
      start.setDate(start.getDate() - 30);
      break;
    case '90d':
      start.setDate(start.getDate() - 90);
      break;
    default:
      start.setDate(start.getDate() - 7);
  }
  
  return {
    start: start.toISOString(),
    end: end.toISOString()
  };
};

function PasswordScreen({ 
  password, 
  setPassword, 
  error, 
  onSubmit 
}: { 
  password: string; 
  setPassword: (p: string) => void; 
  error: string; 
  onSubmit: (e: React.FormEvent) => void;
}) {
  useEffect(() => {
    document.title = 'Mapa de Calor - Analytics Lyra';
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-4 text-center">
          <div className="flex justify-center">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
              <Lock className="w-8 h-8 text-primary" />
            </div>
          </div>
          <div>
            <CardTitle className="text-2xl">Analytics Lyra</CardTitle>
            <CardDescription className="mt-2">
              Esta página contém dados de marketing e conversão.
              <br />
              Digite a senha para acessar.
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-2">
              <Input
                type="password"
                placeholder="Senha de acesso"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoFocus
                className={error ? 'border-destructive' : ''}
              />
              {error && (
                <p className="text-sm text-destructive">{error}</p>
              )}
            </div>
            
            <Button type="submit" className="w-full" size="lg">
              Acessar Dashboard
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

function DashboardContent() {
  const [datePreset, setDatePreset] = useState('7d');
  const [selectedPage, setSelectedPage] = useState<'/' | '/demo' | '/mapa'>(() => {
    const currentPath = window.location.pathname;
    if (currentPath === '/mapa' || currentPath === '/demo') {
      return currentPath as '/mapa' | '/demo';
    }
    return '/';
  });
  const dateRange = useMemo(() => getDateRange(datePreset), [datePreset]);
  const { trackPageView, trackClick } = usePublicTracking();
  
  const { 
    data: conversionRate, 
    refetch: refetchRate,
    isLoading: isLoadingRate,
    error: errorRate
  } = useConversionRate(dateRange);
  
  const { 
    data: funnelData, 
    refetch: refetchFunnel,
    isLoading: isLoadingFunnel
  } = useConversionFunnel(dateRange);
  
  const { 
    data: heatmapData, 
    refetch: refetchHeatmap,
    isLoading: isLoadingHeatmap
  } = useHeatmapData(selectedPage, dateRange);
  
  const { 
    data: topClicks, 
    refetch: refetchClicks,
    isLoading: isLoadingClicks
  } = useTopClicks(dateRange);
  
  const { 
    data: visitorsData, 
    refetch: refetchVisitors,
    isLoading: isLoadingVisitors
  } = useVisitorsOverTime(dateRange);
  
  const { 
    data: avgTime, 
    refetch: refetchAvgTime,
    isLoading: isLoadingAvgTime
  } = useAverageSessionTime(dateRange);
  
  useEffect(() => {
    trackPageView();
    
    const handleClickEvent = (e: MouseEvent) => trackClick(e);
    document.addEventListener('click', handleClickEvent);
    
    return () => document.removeEventListener('click', handleClickEvent);
  }, [trackPageView, trackClick]);
  
  // Log para debug
  useEffect(() => {
    console.log('=== Dashboard State ===');
    console.log('Conversion Rate:', {
      data: conversionRate,
      isLoading: isLoadingRate,
      error: errorRate
    });
    console.log('Date Range:', dateRange);
  }, [conversionRate, isLoadingRate, errorRate, dateRange]);
  
  const handleRefresh = () => {
    console.log('Forçando atualização completa...');
    refetchRate();
    refetchFunnel();
    refetchHeatmap();
    refetchClicks();
    refetchVisitors();
    refetchAvgTime();
  };

  const handleLogout = () => {
    sessionStorage.removeItem(SESSION_KEY);
    window.location.reload();
  };

  useEffect(() => {
    document.title = 'Mapa de Calor - Analytics Lyra';
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-card sticky top-0 z-10 shadow-sm">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <h1 className="text-xl font-bold flex items-center gap-2">
            <BarChart3 className="h-6 w-6 text-primary" />
            Mapa de Calor - Analytics Lyra
          </h1>
          
          <div className="flex items-center gap-3">
            <Select value={datePreset} onValueChange={setDatePreset}>
              <SelectTrigger className="w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="30m">Últimos 30 minutos</SelectItem>
                <SelectItem value="1h">Última hora</SelectItem>
                <SelectItem value="3h">Últimas 3 horas</SelectItem>
                <SelectItem value="today">Hoje</SelectItem>
                <SelectItem value="7d">Últimos 7 dias</SelectItem>
                <SelectItem value="30d">Últimos 30 dias</SelectItem>
                <SelectItem value="90d">Últimos 90 dias</SelectItem>
              </SelectContent>
            </Select>
            
            <Button onClick={handleRefresh} variant="outline" size="sm">
              <RefreshCw className="h-4 w-4 mr-2" />
              Atualizar
            </Button>
            
            <Button onClick={handleLogout} variant="ghost" size="sm">
              <LogOut className="h-4 w-4 mr-2" />
              Sair
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6 space-y-6">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total de Sessões</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {isLoadingRate ? (
                <div className="text-2xl font-bold animate-pulse">Carregando...</div>
              ) : (
                <div className="text-2xl font-bold">
                  {(conversionRate?.total_sessions ?? 0).toLocaleString()}
                </div>
              )}
              <p className="text-xs text-muted-foreground">visitantes únicos</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Conversões</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {isLoadingRate ? (
                <div className="text-2xl font-bold animate-pulse">Carregando...</div>
              ) : (
                <div className="text-2xl font-bold">
                  {(conversionRate?.converted_sessions ?? 0).toLocaleString()}
                </div>
              )}
              <p className="text-xs text-muted-foreground">formulários enviados</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Taxa de Conversão</CardTitle>
              <BarChart3 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {isLoadingRate ? (
                <div className="text-2xl font-bold animate-pulse">Carregando...</div>
              ) : (
                <div className="text-2xl font-bold">
                  {conversionRate?.conversion_rate ?? 0}%
                </div>
              )}
              <p className="text-xs text-muted-foreground">dos visitantes</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Tempo Médio</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {isLoadingAvgTime ? (
                <div className="text-2xl font-bold animate-pulse">Carregando...</div>
              ) : (
                <div className="text-2xl font-bold">
                  {avgTime?.average_minutes 
                    ? `${avgTime.average_minutes.toFixed(1)}min`
                    : '0min'
                  }
                </div>
              )}
              <p className="text-xs text-muted-foreground">no site</p>
            </CardContent>
          </Card>
        </div>
        
        <div className="grid gap-6 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>Visitantes ao Longo do Tempo</CardTitle>
              <CardDescription>Número de visitantes únicos por dia</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={visitorsData || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="visitors" stroke="hsl(var(--primary))" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle>Funil de Conversão</CardTitle>
              <CardDescription>Jornada do visitante até a conversão</CardDescription>
            </CardHeader>
            <CardContent>
              <ConversionFunnel data={funnelData || []} />
            </CardContent>
          </Card>
        </div>
        
        <Card>
          <CardHeader>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div className="flex-1">
                <CardTitle>Mapa de Calor de Cliques</CardTitle>
                <CardDescription className="flex items-center gap-2 mt-1">
                  Visualização das áreas mais clicadas 
                  {heatmapData && heatmapData.length > 0 && (
                    <Badge variant="secondary" className="ml-2">
                      {heatmapData.length} zonas detectadas
                    </Badge>
                  )}
                </CardDescription>
              </div>
              <Select value={selectedPage} onValueChange={(v) => setSelectedPage(v as '/' | '/demo' | '/mapa')}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="/">Home</SelectItem>
                  <SelectItem value="/demo">Demo</SelectItem>
                  <SelectItem value="/mapa">Painel Analytics</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            {isLoadingHeatmap ? (
              <div className="flex items-center justify-center h-96 bg-muted/30 rounded-lg">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                  <p className="text-muted-foreground">Carregando dados de cliques...</p>
                </div>
              </div>
            ) : !heatmapData || heatmapData.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-96 bg-muted/30 rounded-lg border-2 border-dashed">
                <AlertCircle className="h-16 w-16 text-muted-foreground mb-4" />
                <p className="text-lg font-medium mb-2">Nenhum clique registrado ainda</p>
                <p className="text-sm text-muted-foreground mb-4">
                  Clique em alguns elementos da página "{selectedPage}" e depois atualize o painel
                </p>
                <Button onClick={refetchHeatmap} variant="outline" size="sm">
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Atualizar Agora
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    {heatmapData.length} {heatmapData.length === 1 ? 'zona de clique detectada' : 'zonas de clique detectadas'}
                  </p>
                  <Button onClick={refetchHeatmap} variant="ghost" size="sm">
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Atualizar
                  </Button>
                </div>
                <HeatmapViewer data={heatmapData} pagePath={selectedPage} />
              </div>
            )}
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle>Top 10 Elementos Clicados</CardTitle>
            <CardDescription>Elementos mais interagidos pelos visitantes</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Elemento</TableHead>
                    <TableHead>Texto</TableHead>
                    <TableHead>Página</TableHead>
                    <TableHead className="text-right">Cliques</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {topClicks && topClicks.length > 0 ? (
                    topClicks.map((click, index) => (
                      <TableRow key={index}>
                        <TableCell className="font-mono text-xs">
                          {click.element_id ? (
                            <Badge variant="outline">{click.element_id}</Badge>
                          ) : (
                            <Badge variant="secondary">({click.x_coordinate}, {click.y_coordinate})</Badge>
                          )}
                        </TableCell>
                        <TableCell className="max-w-xs truncate">
                          {click.element_text || 
                            <span className="text-muted-foreground italic">
                              Clique em ({click.x_coordinate}, {click.y_coordinate})
                            </span>
                          }
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{click.page_path}</Badge>
                        </TableCell>
                        <TableCell className="text-right font-semibold">{click.count}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-muted-foreground">
                        Nenhum clique registrado no período selecionado
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

export default function Mapa() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const auth = sessionStorage.getItem(SESSION_KEY);
    if (auth === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!password) {
      setError('Por favor, digite a senha.');
      return;
    }

    if (password === PASSWORD) {
      sessionStorage.setItem(SESSION_KEY, 'true');
      setIsAuthenticated(true);
      setError('');
    } else {
      setError('Senha incorreta. Tente novamente.');
      setPassword('');
    }
  };

  if (!isAuthenticated) {
    return (
      <PasswordScreen 
        password={password}
        setPassword={setPassword}
        error={error}
        onSubmit={handlePasswordSubmit}
      />
    );
  }

  return <DashboardContent />;
}
